import { control, field } from '../fields'
import Modal from './Modal'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Ruler, Video } from 'lucide-react'
import { clockTime } from '../api/attendance'
import { formatFullDate, today } from '../api/calendar'
import { asChoreoInput, asChoreoRow, isChoreoFile, type ChoreoLinkRow } from '../api/choreoLinks'
import {
  agesProblem,
  archiveClass,
  createClass,
  FILING_CONFLICTS_KEY,
  fetchClass,
  fetchNewClassOptions,
  musicUrlProblem,
  designateMakeup,
  saveClass,
  setClassTeachers,
  whyStillNotOnSite,
  years,
  type ClassAdminView,
  type ArchiveRefusal,
  type ClassSession,
} from '../api/classAdmin'
import { assignClass, fetchClassTypes, type ClassTypeView } from '../api/classTypes'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { fetchStaff } from '../api/bookings'
import { useCan } from '../useSession'
import BackLink from './BackLink'

/*
 * The pair moved to `src/fields.ts` (D-515), where its reasoning now lives —
 * this file's copy was one of eighteen, and the argument about `w-full` beating
 * a narrower class is true of every one of them, not just this screen.
 *
 * The Ages and When-it-meets rows below still layer `w-20` and `w-[8.5rem]` on
 * `field` and are silently full-width; they survive it because a nowrap row
 * shrinks equal items evenly and it reads as intentional. Left alone, as before.
 */

/**
 * Removing a class the studio made by mistake (D-413).
 *
 * <b>Two shapes of answer, and they are not the same act.</b> Dancers still
 * enrolled is a <em>number she has to see</em> before it happens — it takes the
 * class off their schedules too. Attendance already marked is a <em>refusal</em>:
 * those records are somebody's percentage, the figure that decides whether she
 * may compete, and there is no sentence that makes destroying it reasonable to
 * click past. The server decides which; this renders what it says.
 *
 * <b>Archived, not deleted</b> (A-12). The sessions go with it, because they are
 * drawn from the class (A-1) and leaving them puts dated rows on a calendar
 * belonging to a class that no longer exists.
 */
function RemoveClass({
  classId,
  name,
  onClose,
  onRemoved,
}: {
  classId: string
  name: string
  onClose: () => void
  onRemoved: () => void
}) {
  const queryClient = useQueryClient()
  const [refusal, setRefusal] = useState<ArchiveRefusal | null>(null)

  const remove = useMutation({
    mutationFn: (confirm: boolean) => archiveClass(classId, confirm),
    onSuccess: (result) => {
      if (!result.ok) {
        setRefusal(result.refusal)
        return
      }

      // The list behind the panel still holds the row, and the calendar and
      // the public page may both have shown it.
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })
      queryClient.invalidateQueries({ queryKey: ['calendar'] })
      onRemoved()
    },
  })

  /*
   * Which refusals she may overrule. The enrolment count, and — since D-859 — the
   * Jackrabbit one, which is a warning about the *other* system rather than about this
   * class's data. **Attendance still cannot**: it is somebody's percentage, and there is
   * no sentence that makes destroying it reasonable to click past.
   */
  const mayConfirm = refusal?.enrolled !== undefined || refusal?.imported === true

  return (
    /*
      <b>On the shared shell</b> (D-515). It had a role and a name but no
      `aria-modal`, and nothing closed it on Escape — on the one dialog in the
      panel whose answer cannot be undone by typing something else. The
      backdrop was a button carrying its own accessible name, which is a
      control in the tab order that is really the dim area.
    */
    <Modal
      role="alertdialog"
      onDismiss={onClose}
      label="Remove this class"
      overlayClassName="fixed inset-0 z-[60] grid place-items-center bg-black/40 px-5"
      panelClassName="relative w-full max-w-[26rem] rounded-2xl border border-line bg-surface p-5"
    >
        <h2 className="font-display text-xl">Remove this class?</h2>

        <p className="mt-2 text-[13px] wrap-anywhere text-ink-2">{name}</p>

        <p className="mt-2 text-[12.5px] text-ink-3">
          It comes off the classes list, the calendar and the website. Nothing is deleted, so
          the studio&rsquo;s records keep it.
        </p>

        {refusal && (
          <div
            role="alert"
            className={`mt-3 rounded-lg border px-3 py-2 text-[12.5px] ${
              mayConfirm ? 'border-warn bg-warn-bg text-warn' : 'border-crit bg-crit-bg text-crit'
            }`}
          >
            <p className="font-semibold">{refusal.title}</p>
            <p className="mt-0.5">{refusal.detail}</p>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          {/*
            The wording follows the answer: the first press asks, and a second
            press only appears where going ahead is actually allowed. An
            attendance refusal leaves Cancel as the only way out, which is the
            honest shape — there is nothing to confirm.
          */}
          {(!refusal || mayConfirm) && (
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate(refusal !== null)}
              className="rounded-lg bg-crit px-4 py-2 text-[13px] font-semibold text-on-crit disabled:opacity-40"
            >
              {remove.isPending ? 'Removing...' : refusal ? 'Remove it anyway' : 'Remove it'}
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
          >
            Cancel
          </button>
        </div>
    </Modal>
  )
}

/**
 * Offering one of this class's sessions as a makeup (D-281).
 *
 * <b>Per session, not per class</b> (A-1). The studio runs one of these roughly
 * monthly, and the class behind them may hold several across a season — a
 * Saturday for ballet 2/3 in October and one for jazz in November.
 *
 * <b>Saved on its own, not with the panel.</b> It writes to a different record
 * and the panel's Save writes the class; folding the two together would mean a
 * failed designation looked like a failed class edit, and undoing one would
 * undo the other.
 */
/**
 * Run this make-up session again, on one more date (D-837).
 *
 * <b>`asked`, 2026-09-27</b>: *"for the reoccurring makeup weekend classes… there
 * should be a way to just be able to schedule another makeup class of that same
 * type that only occurs once more on the date selected… we prob should just make it
 * to where you can open the class and set another date to have it occur again."*
 *
 * <b>It makes a new one-date class rather than adding a date to this one</b>, and
 * that is the decision rather than a shortcut. A `StudioClass` carries one weekday
 * and one start time, so a second date on this class would be **off-pattern for
 * ever** — `ScheduledDates()` would never generate it, `OffPatternSession` would
 * report it, and a report that fires on every make-up ever made is one nobody
 * reads. Widening the class's own dates instead is worse: the cadence is weekly, so
 * one date in November turns October's single Saturday into every Saturday between
 * (D-402's *one Saturday, not forty*, from the other end).
 *
 * <b>Only for a class that meets on one date and is designated</b>, which is what a
 * weekend make-up is (D-402). A season class with offers on some of its nights is
 * not a make-up class and has nothing to run again.
 *
 * <b>It lands on the new class</b> rather than staying here, because the copy is
 * everything but the date and the next thing she may want is to change the room.
 */
function RunItAgain({ data }: { data: ClassAdminView }) {
  const navigate = useNavigate()
  const [on, setOn] = useState('')

  /*
   * The session being copied: designated, which is what makes this a make-up
   * rather than a workshop (D-402).
   *
   * <b>Two designated sessions are deliberately not copyable</b>, and both were
   * excluded in SQL by the template list this replaces (D-410):
   *
   * A <b>private repair</b> carries a category and belongs to one dancer (D-406).
   * It is created on the class whose absence it repairs, so a make-up class can
   * come to hold one — and copying it would offer one girl's lesson to the studio.
   *
   * A session designated for a <b>routine</b> is offered to nobody: `Reach`
   * refuses a choreo absence before it compares anything (D-405), so the copy
   * would fill nothing and expire without a word. The create endpoint refuses it
   * outright, and a control whose value is thrown away is one that lies (D-204).
   */
  const designated = data.sessions.find(
    (s) => s.repairs && s.repairs !== 'Choreo' && !s.isCancelled && !s.isPrivateRepair,
  )

  /*
   * <b>The primary teacher travels</b> (D-404, D-410, D-536).
   *
   * A new class takes one, and the teacher is who a level-up make-up asks for
   * approval — an unassigned one is what left that request reaching nobody.
   *
   * <b>`[0]` is the primary, not an arbitrary pick.</b> The endpoint orders the
   * assignments `IsPrimary` first (D-536), which is also the order the card shows
   * and the editor re-sends. D-775's warning is about `OrderBy(x => x.Id)`, and it
   * does not reach an explicitly ordered projection.
   *
   * <b>A student teacher is not a teacher</b> (D-538). `instructorStaffIds` is
   * every assignment and `studentTeacherStaffIds` is the assisting subset of it,
   * so the teaching set is the difference — the same subtraction `TeacherSet`
   * makes. Without it, a class with one teacher and one assistant carried nobody
   * while claiming to have two teachers, and a class assisted by a student
   * teacher alone would have written her in as its primary instructor.
   */
  const teaching = data.instructorStaffIds.filter(
    (id) => !data.studentTeacherStaffIds.includes(id),
  )
  const carriedTeacher = teaching[0] ?? null

  const again = useMutation({
    mutationFn: () =>
      createClass({
        name: data.name,
        studioProgramId: data.studioProgramId,
        dayOfWeek: data.dayOfWeek,
        startTime: data.startTime,
        endTime: data.endTime,
        roomId: data.roomId,

        /*
         * **The kind deliberately does not travel** — the same reason the select
         * this replaced did not copy it (D-410). A kind carries marketing: a name,
         * a photograph and D-261's gate on what the public page shows. A make-up
         * session is not marketed; it exists so a dancer can repair an absence.
         */
        classTypeId: null,

        makeupCategory: designated?.repairs ?? null,
        style: designated?.style ?? null,
        levelBand: designated?.levelBand ?? null,

        // It is ours, so the name is not checked against Jackrabbit's (D-207).
        alsoInJackrabbit: false,

        runsOn: on,
        isMakeupSession: true,
        makeupMinimumSignups: designated?.minimumSignups ?? null,
        teacherStaffId: carriedTeacher,
      }),

    onSuccess: (made) => navigate(`/admin/classes/${made.classId}`),
  })

  if (!data.isOurs || !data.meetsOnOneDate || !designated) return null

  return (
    <div className="mt-2 flex w-full flex-col gap-1 border-t border-line pt-2">
      <span className="text-[12px] font-semibold text-ink-2">Run this one again</span>

      {/*
        <b>It names what travels rather than saying "everything else"</b>. D-410
        records that exact phrase being written down and then found false about the
        kind; it is false here about the size, the age range and the description
        too, none of which a new class can carry. A promise wider than the code is
        the D-204 shape aimed at the copy instead of the control.
      */}
      <p className="text-[12px] text-ink-3">
        Makes another session on the day you pick, offering the same absences &mdash; the
        name, the hours, the room and the teacher come across, and are yours to change
        afterwards.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-ink-2">The day it runs</span>
          <input
            type="date"
            value={on}
            /*
              <b>No day that has already gone.</b> The one class this control
              appears on is in the past, so a mistyped month lands behind today
              easily — and a make-up dated behind today is offered to nobody,
              because the portal clamps its window to today and always has. The
              server refuses it as well; this is so she never reaches the refusal.
            */
            min={today()}
            onChange={(e) => setOn(e.target.value)}
            className={field}
          />
        </label>

        {/*
          <b>The outlined accent, the same rank as "Offer this session" below</b>
          (D-383, D-500). It carried the *input* treatment at first, which has no
          `disabled:` rule of its own — so the panel's one act rendered as an empty
          text box, and being disabled until she picks a day showed on screen as
          nothing at all.
        */}
        <button
          type="button"
          disabled={!on || again.isPending}
          onClick={() => again.mutate()}
          className="rounded-lg border border-accent px-3 py-1.5 text-[12.5px] font-semibold text-accent hover:bg-accent-soft disabled:opacity-40"
        >
          {again.isPending ? 'Making it…' : 'Run it again'}
        </button>
      </div>

      {/*
        `text-crit`, not `text-bad` - there has never been a `--color-bad` token,
        so this rendered as ordinary body text while carrying `role="alert"`
        (D-865, found by the check that now fails on a class generating no CSS).
      */}
      {again.isError && (
        <p role="alert" className="text-[12px] text-crit">
          {again.error instanceof Error ? again.error.message : 'Could not make that one.'}
        </p>
      )}
    </div>
  )
}

function MakeupSessions({
  classId,
  sessions,
  isOurs,
  meetsOnOneDate,
}: {
  classId: string
  sessions: ClassSession[]
  /** True for a class the studio made rather than imported (D-207). */
  isOurs: boolean
  /** Whether the list is dated from today, or shows its one session whatever the date (D-837). */
  meetsOnOneDate: boolean
}) {
  const queryClient = useQueryClient()

  /*
   * **Several dates at once** (D-411). `asked`, annotated on a screenshot of
   * this control: *"She should be able to select mulitple dates, as they offer
   * makeup classes once a month."*
   *
   * **A tick list, because the owner already told us that shape works.** D-359
   * shipped the day dialog he described as *"much better to be able to do
   * multiple at a time"*, and this is the same act: choose the dates, say the
   * thing once, save once.
   */
  const [picked, setPicked] = useState<string[]>([])
  const [repairs, setRepairs] = useState('')
  const [style, setStyle] = useState('')
  const [levels, setLevels] = useState('')
  const [minimum, setMinimum] = useState('')

  /*
   * The form is filled from the *first* ticked session, and saving writes it
   * to all of them. Ticking a second date is "and this one too", not a request
   * to merge two designations — which would have no answer when they differ.
   */
  const session = sessions.find((s) => s.occurrenceId === picked[0])

  /** How many of this class's sessions families can actually see (D-411). */
  const offered = sessions.filter((s) => s.repairs && !s.isCancelled).length

  const parsedMinimum = Number.isInteger(Number(minimum.trim())) && minimum.trim() !== ''
    ? Number(minimum.trim())
    : null

  // Load whatever the chosen session already offers, so opening one she has
  // already set up shows what it says rather than an empty form.
  useEffect(() => {
    if (!session) return

    // Or "Saved." sits beside a session that has never been written.
    save.reset()

    setRepairs(session.repairs ?? '')
    setStyle(session.style ?? '')
    setLevels(session.levelBand ?? '')
    setMinimum(session.minimumSignups?.toString() ?? '')

    // eslint-disable-next-line react-hooks/exhaustive-deps -- see below

    /*
     * <b>Keyed on which session, not on the session object</b> (D-514).
     *
     * It was `[session]`, and saving <em>changes that object</em>: `onSuccess`
     * patches the class query so the ticked row carries its new designation, so
     * a new array with a new row comes back, so this effect re-ran and called
     * `save.reset()` on the save that had just succeeded. The confirmation
     * appeared for a single frame and vanished.
     *
     * Nobody had noticed because nothing tested it — and it is *why* D-514's
     * warning could not be made to render: the message was correct, the count
     * was correct, and the success state was being wiped underneath it. An hour
     * of looking for the fault in the new code, and it was a year-old effect
     * dependency.
     *
     * The occurrence id is the honest key: what this effect is for is loading
     * the form when she picks a <em>different</em> session. Its own values
     * changing is the save landing, which is the one moment it must not fire.
     */
  }, [session?.occurrenceId])

  /*
   * <b>How many classes the last save can repair an absence from</b> (D-514).
   *
   * Held here rather than read off the mutation's own `data`, which came back
   * undefined at render time for reasons an hour of bisecting did not explain —
   * so the warning below never appeared while every other symptom said the save
   * had worked. The success callback is handed the same value directly, and it
   * is one less thing between the server's answer and the sentence on screen.
   *
   * Cleared when a save starts, so what is on screen is only ever about the
   * most recent one.
   */
  const [reach, setReach] = useState<number | null>(null)

  const save = useMutation({
    onMutate: () => setReach(null),
    mutationFn: () =>
      designateMakeup(classId, picked, {
        repairs: repairs || null,
        style: style.trim() || null,
        levelBand: levels.trim() || null,
        // Parsed rather than coerced: a number input reports '' for text the
        // browser is still showing, so `Number()` alone turns 'abc' into the
        // studio default without saying so.
        minimumSignups: parsedMinimum,
      }),
    /*
     * <b>Patched in place, never re-fetched.</b>
     *
     * Invalidating the class query hands the panel a new `data` object, its
     * load effect re-runs, and every unsaved edit in the form above is
     * overwritten from the server - a description she was halfway through
     * typing, gone, with no message and a footer that then reads 'Saved'.
     * Only one row of one field actually changed, so only that is written.
     */
    onSuccess: (result) => {
      setReach(result.reaches);

      queryClient.setQueryData(
        ['admin-class', classId],
        (current: ClassAdminView | undefined) =>
          current === undefined
            ? current
            : {
                ...current,
                sessions: current.sessions.map((s) =>
                  // Every date that was ticked, so the count and the Offered
                  // marks beside them agree with what was just saved (D-411).
                  picked.includes(s.occurrenceId)
                    ? {
                        ...s,
                        repairs: (repairs || null) as ClassSession['repairs'],
                        style: style.trim() || null,
                        levelBand: levels.trim() || null,
                        minimumSignups: minimum.trim() ? Number(minimum) : null,
                      }
                    : s,
                ),
              },
      )
    },
  })

  /*
   * <b>Only a class the studio made</b> (D-281).
   *
   * Downstream, a designated session *is* a one-off: it takes a signup minimum,
   * is read by the ladder rather than exactly since D-401 - top rung first - and hands its
   * entitlement back if it never fills (D-279). Applying that to a class already
   * running weekly inverts scenario 67 outright - the class certainly ran, and
   * every booking against it would be released as though it had not.
   *
   * A one-off is a class the studio made for that Saturday (D-207), so that is
   * the line. An imported weekly class is not offered the control at all rather
   * than being offered it and refused.
   */
  if (!isOurs) {
    return (
      <p className="text-[12.5px] text-ink-3">
        A makeup session is its own class. Add one for the day you want to run it, and it can
        be offered here.
      </p>
    )
  }

  /*
   * <b>Two empty states, because the list is dated for one kind of class and not
   * the other</b> (D-837). A season class is shown only what is still to come, so
   * empty means the season is behind it. A class meeting on one date is shown its
   * session whatever the date, so empty means there is no session at all — which
   * is what a class with no start time looks like (D-84). One sentence for both
   * would name a filter that is not applied and send her looking for the wrong
   * thing (D-209).
   */
  if (sessions.length === 0) {
    return (
      <p className="text-[12.5px] text-ink-3">
        {meetsOnOneDate
          ? 'This class has no session on the calendar at all, so there is nothing to offer. A class with no start time meets on no dates.'
          : 'This class has no sessions still to come, so there is nothing to offer.'}
      </p>
    )
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {/*
        **What families can actually see right now** (D-411).

        `asked`: *"Once a makeup is scheduled, how are parents signing up? it
        is not showing as an option on a parents account."* The class-level
        Makeups block above had been filled in and this one had not — two
        controls that read as the same thing, and nothing said which one puts a
        session on a family's list. This is that sentence, and it answers the
        question without opening anything.
      */}
      <p className="text-[11.5px] text-ink-3">
        {offered === 0
          ? 'No session is offered yet, so this class is on nobody’s makeup list.'
          : `${offered} of ${sessions.length} sessions ${
              offered === 1 ? 'is' : 'are'
            } offered to families.`}
      </p>

      {/*
        A tick list rather than a dropdown, so a monthly makeup is one save
        instead of nine (D-411). Scrolls at the same height the day dialog uses
        — a weekly class the studio made can carry forty dates.
      */}
      <ul className="flex max-h-[42vh] flex-col gap-0.5 overflow-y-auto">
        {sessions.map((s) => (
          <li key={s.occurrenceId}>
            <label
              className={`flex w-full items-center gap-2 rounded-md px-1 py-1 text-[12.5px] ${
                s.isCancelled ? 'text-ink-3' : 'text-ink-2 hover:bg-surface-2'
              }`}
            >
              <input
                type="checkbox"
                /*
                 * <b>And not a session that has already run</b> (D-837). This list
                 * was dated from today until a one-date make-up had to show the
                 * session it exists for after the date passed — which put a
                 * *past* row in front of a tick box for the first time, and the
                 * endpoint has no date guard. Withdrawing the offer from an
                 * evening that happened would be editing history: the class met,
                 * the visitors were marked, and their repairs are already filed.
                 *
                 * `today()` rather than a UTC date, which is *tomorrow* after
                 * about 7pm Central — inside studio hours (D-485).
                 */
                disabled={s.isCancelled || s.date < today()}
                checked={picked.includes(s.occurrenceId)}
                onChange={(e) =>
                  setPicked((cur) =>
                    e.target.checked
                      ? [...cur, s.occurrenceId]
                      : cur.filter((id) => id !== s.occurrenceId),
                  )
                }
                className="size-4 shrink-0"
              />
              <span className="min-w-0 flex-1 wrap-anywhere">{sessionLabel(s)}</span>

              {/*
                Which dates already carry an offer, so she can see the pattern
                she is adding to rather than reconstructing it one tick at a
                time.
              */}
              {s.repairs && !s.isCancelled && (
                <span className="shrink-0 text-[11px] font-semibold text-good">Offered</span>
              )}
            </label>
          </li>
        ))}
      </ul>

      {session && (
        <>
          {/*
            <b>Named on screen, exactly as the block above is</b> (D-510).

            D-497 gave the class-level trio visible labels because a filled box
            shows a bare word with nothing saying what it is. It stopped one
            block short: these four had the same fault, one scroll further
            down, and the screenshot behind this shows it plainly &mdash;
            <i>tap</i> and <i>2/3</i> floating unlabelled while the empty
            <i>Min</i> beside them still had its placeholder to explain itself.
            A label that vanishes when the field is used is worst on exactly
            the fields somebody has already filled in.

            <b>The names are deliberately not the same four words.</b> The
            block above says what a missed session of this class is repaired
            by; this says what one Saturday is offered as. Reusing
            <i>Style</i> and <i>Level</i> here would put two identically
            labelled pairs on one panel, which is the confusion the owner
            reported in D-411, not a fix for it.
          */}
          <div className="flex w-full flex-wrap items-end gap-2">
            <label className="flex min-w-[8rem] flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-3">Offered as</span>
              <select
                value={repairs}
                onChange={(e) => setRepairs(e.target.value)}
                aria-label="What kind of absence this repairs"
                className={control}
              >
                <option value="">Not offered</option>
                {/*
                  **No Choreography here** (D-405). This select designates what
                  a session *repairs*, and a routine is repaired in a private —
                  so the matcher refuses a choreo designation outright and a
                  session carrying one would advertise a style and a band, be
                  offered to nobody, and expire without a word.

                  Offering it would be a control whose value is thrown away
                  (D-204). The server refuses it too, because this panel is not
                  the only caller.

                  It is still a perfectly good value for a *class*, which is how
                  a routine reaches the private path at all — that select is in
                  `NewClassDialog` and keeps it.
                */}
                <option value="Tech">Technique</option>
              </select>
            </label>

            <label className="flex min-w-[7rem] flex-1 flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-3">Style it covers</span>
              <input
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="Ballet, tap, jazz…"
                aria-label="Style it covers"
                className={control}
              />
            </label>

            <label className="flex w-24 flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-3">Pitched at</span>
              <input
                value={levels}
                onChange={(e) => setLevels(e.target.value)}
                placeholder="3/4"
                aria-label="The level this session is pitched at"
                className={`${control} tabular text-center`}
              />
            </label>

            <label className="flex w-24 flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-3">Fewest dancers</span>
              <input
                type="number"
                min={1}
                value={minimum}
                onChange={(e) => setMinimum(e.target.value)}
                placeholder="Default"
                aria-label="Dancers needed before it runs"
                className={`${control} tabular text-center`}
              />
            </label>
          </div>

          {/*
            Derived, not stored — the count is worked out every time anybody
            looks, because there is no scheduler and this must never be
            something Tonya remembers to check (D-279).
          */}
          {session.repairs && (
            <p className="text-[11.5px] text-ink-3">
              {session.signups} signed up
              {session.minimumSignups !== null && (
                <>
                  {' '}
                  of {session.minimumSignups} needed &mdash;{' '}
                  {session.signups >= session.minimumSignups
                    ? 'it will run'
                    : 'not enough yet'}
                </>
              )}
            </p>
          )}

          {/*
            **This said "levels it covers", and it stopped being true** (D-401).

            While a session matched its band exactly, "covers" was the right
            word and writing 2/3 meant "my level 2 and 3 dancers". The band is
            now a rung on the ladder, read top-first: a session written 2/3
            reaches a missed 3 and a missed 4, and a level-2 dancer is one below
            it — which is her teacher's call, not an offer.

            So somebody typing 2/3 for her level 2s would have had them silently
            left out, with nothing on screen to discover it from: an empty offer
            rather than an error. Found by a cold audit reading the copy against
            the new rule.
          */}
          <p className="text-[11.5px] text-ink-3">
            Leave the minimum empty to use the studio default. The level is where the session is
            pitched &mdash; dancers at that level and one above it can book in, and anybody higher
            needs their teacher&rsquo;s say-so.
          </p>

          <div className="flex items-center gap-3">
            {/*
              <b>It looks like the act it is</b> (D-510).

              `asked`: *"This offer this session button is confusing and easily
              missed. Maybe look at this whole thing and make it more user
              friendly."* It was a grey outlined button sitting between a
              paragraph of explanation and the next form field, so the one
              control that puts a class on other dancers&rsquo; makeup lists
              read as one more box in a stack of boxes.

              **Outlined accent rather than filled** (D-383, D-500). The
              panel&rsquo;s own Save below is the filled `bg-accent` button;
              two filled buttons on one panel would only move the question
              from *which of these is the act* to *which of these two saves
              what*. The outline is the treatment the owner picked for exactly
              this rank &mdash; important, but not the page&rsquo;s primary
              act.
            */}
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate()}
              className="rounded-lg border border-accent px-3 py-1.5 text-[12.5px] font-semibold text-accent hover:bg-accent-soft disabled:opacity-40"
            >
              {/*
                **It says how many it is about to write** (D-411).

                It read *"Offer this session"* however many dates were ticked,
                which was found by opening the screen rather than by any test:
                the whole point of the tick list is that it is more than one,
                and a control that understates what it does is the shape D-204
                keeps naming.
              */}
              {save.isPending
                ? 'Saving...'
                : repairs
                  ? picked.length > 1
                    ? `Offer these ${picked.length} sessions`
                    : 'Offer this session'
                  : picked.length > 1
                    ? `Stop offering these ${picked.length}`
                    : 'Stop offering it'}
            </button>

            {save.isSuccess && !save.isPending && reach !== 0 && (
              <span className="text-[11.5px] text-ink-3">Saved.</span>
            )}
          </div>

          {save.isError && (
            <p role="alert" className="text-[12.5px] text-crit">
              {(save.error as Error).message}
            </p>
          )}

          {/*
            <b>Saved, and reaching nobody</b> (D-514).

            `asked`, beside the report that became D-510: *she offered a session
            here and the parent portal then found nothing*. A session reaches a
            family only when its kind, its style and its level all line up with
            the class they missed — so a style with a typo in it, or a band that
            reaches nothing, saved happily and was offered to no-one, with
            <i>Saved.</i> as the only feedback.

            <b>Said after the save rather than refusing it.</b> D-51 and D-227's
            line holds: only the studio knows whether this session or the class
            it should match is the thing that is wrong. Filing the session now
            and fixing the class tomorrow is also perfectly reasonable, and a
            refusal would make it impossible.

            <b>Zero, never null.</b> Withdrawing an offer reaches nothing by
            definition, and a warning there would fire on the one act that is
            never a mistake — so the server distinguishes the two rather than
            leaving this to guess.
          */}
          {save.isSuccess && !save.isPending && reach === 0 && (
            <p
              role="status"
              className="rounded-lg border border-warn bg-warn-bg px-3 py-2 text-[11.5px] text-warn"
            >
              Saved &mdash; but no class in the studio matches it, so no dancer will be offered
              it. A session only repairs an absence of the same <strong>kind and style</strong>,
              at a level it reaches. Either this session&rsquo;s style is not one the classes
              carry, or those classes have no style filled in yet &mdash; open one and check
              its <em>Makeups</em> fields.
            </p>
          )}
        </>
      )}
    </div>
  )
}

/** "Sat 24 October, 10:00am" — what she is looking for is the date. */
function sessionLabel(session: ClassSession): string {
  const label = formatFullDate(session.date)
  const time = session.startTime ? `, ${clockTime(session.startTime)}` : ''
  const off = session.isCancelled ? ' (cancelled)' : ''

  // "offered" used to be appended here as well. It is a badge beside the row
  // now (D-411), and two places saying one thing is how the two drift.
  return `${label}${time}${off}`
}

/**
 * One class, opened from the list (D-205).
 *
 * <b>A panel rather than a page</b>, because correcting five classes should be
 * five clicks. The old screen replaced the whole list with an editor and gave
 * back an "All classes" link, so the list scrolled to the top between every
 * one — enough friction that a batch of small corrections stopped being worth
 * starting.
 *
 * <b>Ownership is marked per field, not per section.</b> The screen it replaces
 * had "Yours to set" and "Comes from Jackrabbit" as two blocks, which made the
 * layout a statement about who owns what — so every field that came home (ages,
 * measurements, descriptions) forced a redesign. A lock beside the field says
 * the same thing and costs nothing when it moves (D-124, D-205).
 */
export default function ClassRecordView({
  classId,
  kinds,
  kindsRefused = false,
}: {
  classId: string
  kinds: ClassTypeView[] | undefined
  /** True once the kinds have been asked for and refused (D-566). */
  kindsRefused?: boolean
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const mayMeasure = useCan("RecordMeasurements")

  const { data, isPending, isError } = useQuery({
    queryKey: ['admin-class', classId],
    queryFn: () => fetchClass(classId),
  })

  // The confirm step for removing the class (D-413). Its own dialog, because
  // it is the one act on this panel that cannot be undone by typing something
  // else, and it has a number to show before it happens.
  const [removing, setRemoving] = useState(false)

  const [name, setName] = useState('')
  const [routineName, setRoutineName] = useState('')
  const [musicUrl, setMusicUrl] = useState('')
  const [choreoLinks, setChoreoLinks] = useState<ChoreoLinkRow[]>([])
  const [description, setDescription] = useState('')
  const [onSite, setOnSite] = useState(false)

  // Held as text, so clearing a box is "no limit" rather than the zero a number
  // input reports — and "ages 0 and up" is not what anybody meant to type.
  const [minAge, setMinAge] = useState('')
  const [maxAge, setMaxAge] = useState('')

  // What the class is for makeup purposes, and the levels it covers (D-280).
  // All three are parsed out of a class name until somebody corrects them, and
  // a wrong level silently withholds a makeup a family is owed.
  const [makeupCategory, setMakeupCategory] = useState('')
  const [style, setStyle] = useState('')
  const [levelBand, setLevelBand] = useState('')

  /*
   * Whether the studio asks anybody to make this class up (D-658).
   *
   * Beside the three above because it belongs in the same fieldset, and
   * outside `makeupDirty` because it is not one of them: those three are
   * Jackrabbit's to seed and this one has never existed there, so there is
   * nothing to claim and nothing an import could take back.
   */
  const [noMakeup, setNoMakeup] = useState(false)
  const [takesACall, setTakesACall] = useState(false)

  // Only reachable on a class the studio made — an imported one has these
  // rewritten by every import, and the server refuses them (D-207).
  const [day, setDay] = useState('Monday')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [roomId, setRoomId] = useState('')

  /*
   * How often it meets, and the evening that counts from (D-603).
   *
   * Offered on every class, imported or not, which is the opposite of the
   * fields above: an import rewrites the day, the time and the room, and no
   * import has ever touched the dates — so this is the studio's to answer on
   * all twelve of the real alternating classes.
   */
  const [cadence, setCadence] = useState<'Weekly' | 'Fortnightly'>('Weekly')
  const [firstMeeting, setFirstMeeting] = useState('')

  /*
   * Touched, rather than changed. Answering "yes, fortnightly, and it is this
   * week" is the studio claiming the field even when neither value moved, and
   * that claim is the whole point — it is what stops the next import
   * overruling her and what takes the class off the list of twelve (D-206).
   */
  const [meetingTouched, setMeetingTouched] = useState(false)

  useEffect(() => {
    if (!data) return
    setName(data.name)
    setRoutineName(data.routineName ?? '')
    setMusicUrl(data.musicUrl ?? '')
    setChoreoLinks((data.choreoLinks ?? []).map(asChoreoRow))
    setDescription(data.description ?? '')
    setOnSite(data.showInPublicCatalog)
    setMinAge(data.minAgeYears?.toString() ?? '')
    setMaxAge(data.maxAgeYears?.toString() ?? '')
    setMakeupCategory(data.makeupCategory ?? '')
    setStyle(data.style ?? '')
    setLevelBand(data.levelBand ?? '')
    setNoMakeup(data.noMakeup)
    setTakesACall(data.takesACall)
    setDay(data.dayOfWeek)
    setStartTime(clockValue(data.startTime))
    setEndTime(clockValue(data.endTime))
    setRoomId(data.roomId ?? '')
    setCadence(data.cadence)
    setFirstMeeting(data.firstMeeting)
    setMeetingTouched(false)
  }, [data])

  /*
   * <b>The dialog machinery went with the drawer</b> (D-566).
   *
   * This was the last screen onto the shared modal shell (D-515) and the only
   * one that needed more than a hook call — a backdrop, a focus trap, and an
   * Escape that had to stack with <i>Remove this class?</i> so that backing
   * out of the confirmation did not also discard every unsaved edit behind
   * it. None of that is owed by a page: Escape closes nothing, there is
   * nothing behind to trap focus away from, and the confirmation is the only
   * dialog left. <c>RemoveClass</c> keeps its own shell.
   */

  /**
   * Whether the header is showing the class's name — which is the only thing
   * the dialog's own name can point at.
   *
   * One value read by both, because two conditions for one question disagree
   * and the disagreement here is silent: an `aria-labelledby` aimed at an
   * element that is not in the document is not a wrong name, it is no name.
   */
  const heading = !isError && !isPending && data !== undefined

  const save = useMutation({
    mutationFn: () =>
      saveClass(classId, {
        // Only for a class we made; the server refuses it on an imported one (D-633).
        ...(data?.isOurs ? { name: name.trim() } : {}),
        routineName: routineName.trim() || null,
        musicUrl: musicUrl.trim() || null,
        choreoLinks: choreoLinks.map(asChoreoInput),
        description: description.trim() || null,
        /*
         * Style is in the form now, and it rides the flag below (D-280).
         *
         * D-205 kept it out on the grounds that nothing populated it — true of
         * 0 of 185 classes at the time, and no longer true since D-272 made the
         * import seed it and the makeup rules read it. It is sent *only* under
         * `setMakeupFields`, because the panel used to send `style: null`
         * unconditionally and an unguarded `SetStyle` destroyed a seeded style
         * on a routine edit.
         */
        style: style.trim() || null,
        showInPublicCatalog: onSite,
        setAges: true,
        minAgeYears: years(minAge),
        maxAgeYears: years(maxAge),

        /*
         * <b>Only when she actually touched the fieldset.</b>
         *
         * Sending this on every save would claim all three fields for the studio
         * the moment anybody pasted a music link — and the category is not ours
         * to claim. It comes from Jackrabbit's `Cat3`, a real column with a real
         * value, and D-272 is explicit that a name/category disagreement is
         * *reported, never corrected*, because the fix belongs in Jackrabbit.
         * Freezing a wrong one here would mean correcting it at source no longer
         * worked, with no screen saying why.
         */
        setMakeupFields: makeupDirty,
        makeupCategory: makeupCategory || null,
        levelBand: levelBand.trim() || null,
        noMakeup,
        takesACall,
        // Any class now, imported or not (D-635); the server keeps the correction.
        setSchedule: true,
        dayOfWeek: day,
        startTime: startTime || null,
        endTime: endTime || null,
        roomId: roomId || null,

        /*
         * <b>Only when she touched it</b> (D-603), for the same reason the
         * makeup fields ride a flag: saving this on every edit would claim the
         * cadence for the studio the moment anybody pasted a music link, and
         * the next import would then stop correcting a class nobody had
         * looked at. It is also the flag that says "she has answered which
         * week", which is what takes the class off the list of twelve.
         */
        setMeeting: meetingDirty,
        cadence,
        // Never the empty string a cleared date input holds: the save is
        // blocked before it gets here, and this is the second lock on it.
        firstMeeting: firstMeeting || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-class', classId] })

      /*
       * <b>And the mis-filed banner, which this save can answer</b> (D-506).
       *
       * The banner sits on the same screen with this panel open above it, and
       * filing a class here is exactly what takes it off that list. Without
       * this nothing refetches it — `staleTime` is 30s and the component never
       * unmounts — so it went on naming a class the reader had just fixed,
       * which is D-505's complaint in a different place. Worse, filing from
       * the stale row then wrote this save's style back to null.
       */
      queryClient.invalidateQueries({ queryKey: FILING_CONFLICTS_KEY })
    },
  })

  const urlProblem = musicUrlProblem(musicUrl)
  const ageProblem = agesProblem(minAge, maxAge)

  /*
   * What is keeping a ticked class off the site, from the server's own rule
   * (D-261). Reported as of the last save — ticking the box shows what will
   * still be in the way once it is saved, which is the point of saying it here
   * rather than leaving somebody to check the public page.
   */
  const blocked = data ? whyStillNotOnSite(data.notOnSiteBecause, data.kindName) : []

  /*
   * Whether she actually touched the makeup fieldset.
   *
   * This is the flag the save sends, not merely part of the dirty check — see
   * the payload above for why claiming these on an unrelated edit would be
   * worse than leaving them.
   */
  const makeupDirty =
    data !== undefined &&
    (makeupCategory !== (data.makeupCategory ?? '') ||
      style !== (data.style ?? '') ||
      levelBand !== (data.levelBand ?? ''))

  /**
   * Whether she touched the cadence or the first evening (D-603).
   *
   * <b>Confirming what it already says still counts.</b> Answering "yes, it
   * really is fortnightly, and this is the week" is the studio claiming the
   * field, which is exactly what takes the class off the list of twelve — so
   * this cannot be a value comparison alone. It goes true the moment either
   * control is used, and back to false on the reload after a save.
   */
  const meetingDirty =
    data !== undefined && (meetingTouched || firstMeeting !== data.firstMeeting)

  /**
   * A date input clears to an empty string, and an empty string is not a date.
   *
   * Said on the screen rather than sent (D-603). The server registers no
   * `DateOnly` converter, so an empty value fails to deserialise before the
   * handler is reached — a 400 with no message in it, which `saveClass` can
   * only render as "Could not save that (400)". Every other field on this
   * panel would fail with it and nothing would point at the date.
   */
  const meetingProblem =
    meetingDirty && firstMeeting === '' ? 'Give it a date to start from.' : null

  const dirty =
    data !== undefined &&
    (routineName !== (data.routineName ?? '') ||
      musicUrl !== (data.musicUrl ?? '') ||
      JSON.stringify(choreoLinks) !==
        JSON.stringify((data.choreoLinks ?? []).map(asChoreoRow)) ||
      description !== (data.description ?? '') ||
      onSite !== data.showInPublicCatalog ||
      minAge !== (data.minAgeYears?.toString() ?? '') ||
      maxAge !== (data.maxAgeYears?.toString() ?? '') ||
      makeupDirty ||
      // Its own clause, not part of `makeupDirty` — that flag also claims the
      // three seeded fields for the studio, and ticking this claims nothing.
      noMakeup !== data.noMakeup ||
      takesACall !== data.takesACall ||
      meetingDirty ||
      // The name is only hers on a class she made (D-633); when it meets is
      // hers on any class now (D-635).
      (data.isOurs && name.trim() !== data.name) ||
      day !== data.dayOfWeek ||
      startTime !== clockValue(data.startTime) ||
      endTime !== clockValue(data.endTime) ||
      roomId !== (data.roomId ?? ''))

  return (
    <div className="page-shell px-5 py-8 pb-28">
      {/*
        <b>A page, not a drawer</b> (D-566). `asked`, on a screenshot of the
        record open beside the list: <i>"the view class/edit class page seems
        awkwardly attached to the right side of the page and requires
        scrolling and such."</i>

        It was 30rem of a 1715px screen holding every field of the record in
        one column. Two things made a page the cheap answer rather than a
        wider overlay: the class is already in the URL, because D-462 put it
        there so a search hit could open one; and the quick pass this screen
        is really used for - filing a dozen classes after an import - lives
        on the rows of the list itself, so nothing needed the record to stay
        on top of the list it came from.

        The dialog furniture went with it: no backdrop, no focus trap, no
        Escape. A page is left by leaving it.
      */}
      <BackLink to="/admin/classes" label="All classes" />
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Class</p>
        <div className="min-w-0">
          {/*
            **An error is not a slow load** (D-462, applying D-185).

            The guard was `isPending || !data`, and an error satisfies
            neither — so a failed fetch rendered "Loading..." for ever. That
            path was nearly unreachable while the only way in was clicking a
            row a successful list had produced; putting the class in the URL
            makes it a **first-load** path, including through the ~75 seconds
            after a deploy when every database-backed endpoint refuses (D-33).
            This is D-185s bug, on the third screen to have it.
          */}
          {isError ? (
            <p role="alert" className="text-[13px] text-crit">
              That class could not be loaded. It may have been removed.
            </p>
          ) : !heading ? (
            <p className="text-[13px] text-ink-3">Loading...</p>
          ) : (
            <>
              {/*
                <b><code>text-2xl</code>, because this is the page</b>
                (D-335, D-566). It was written as `text-xl` on the reasoning
                that a heading naming a record sits one step down — and
                `headings.test.ts` rejected it, correctly. That step down is
                for a record shown <i>inside</i> a screen that already has a
                page heading above it, which is what `PeopleAdminView` and
                `MissingAttendanceView` do. A record with its own address has
                no heading above it, and `DancerRecordView` — the same shape,
                one door over — has always said `text-2xl`.

                `wrap-anywhere` because a Jackrabbit name is one unbreakable
                machine-shaped token and the part that tells two sections
                apart is at the end of it.
              */}
              <h1 id="class-record-heading" className="font-display text-2xl wrap-anywhere">
                {data.routineName ?? data.name}
              </h1>
              {data.routineName && (
                <p className="mt-0.5 text-[12.5px] text-ink-3 wrap-anywhere">{data.name}</p>
              )}
            </>
          )}
        </div>
      </header>

      {data && (
        <div className="flex flex-col gap-4">
          {/*
            What it meets, first. Somebody opening a class wants to know they
            have the right one before they type anything.

            <b>Across rather than down, now that there is width</b> (D-566).
            As a two-column `dl` in a 30rem drawer these five facts cost
            five rows before a single field; the same five sit on one line
            on a laptop and cost almost no height. `auto-fit` with a floor
            rather than a fixed count, so they wrap to a second row on a
            phone instead of squeezing "1-Purple Room" into a column three
            characters wide.

            <b>No hairlines between them, and that is the fix rather than a
            taste.</b> The first cut drew the grid as one-pixel gaps over a
            `bg-line` parent, so every track the facts did not fill showed as
            a block of border colour — invisible in light mode, obvious in
            dark. Spanning the last cell only moved it: with `auto-fit` the
            column count changes with the width, an imported class has five
            facts and one the studio made has three, and between them they
            leave a gap at some width whatever any one rule pins. One surface
            with space between the pairs cannot have the bug at all.
          */}
          <dl className="grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-x-6 gap-y-3 rounded-xl border border-line bg-surface-2 px-4 py-3 text-[12.5px]">
            {/*
              The day and room were read-only facts here for an imported
              class, because the fieldset below was offered only on a class
              the studio made. It is offered on every class now (D-635), so
              these would be the same two values twice, one of them inert.
            */}
            <Fact label="Programme">{data.programName}</Fact>
            <Fact label="Teachers">
              <TeacherSet
                classId={classId}
                names={data.instructors}
                staffIds={data.instructorStaffIds}
                assistingIds={data.studentTeacherStaffIds}
              />
            </Fact>
            <Fact label="Enrolled">
              <span className="tabular">
                {data.enrolled}
                {data.maxSize !== null && <> of {data.maxSize}</>}
              </span>
            </Fact>
          </dl>

          <p className="flex items-start gap-1.5 text-[11.5px] text-ink-3">
            <Lock size={12} strokeWidth={2.5} className="mt-0.5 shrink-0" />
            Its name comes from Jackrabbit and the import matches enrolments on it. Change that there.
          </p>

          {/*
            Editable on any class, imported or not (D-635).

            This was gated on the studio having made the class (D-207), on the
            grounds that an import rewrites these and an edit would silently
            vanish. Half true, and the wrong half: the importer rewrites the
            time, and never writes the day at all — its diff does not even
            compare it. So a class imported onto the wrong weekday could be
            corrected nowhere, here or at source, and three of the studio's
            classes contradict the day written in their own name.

            Correcting it here claims the schedule, and the next import leaves
            it alone rather than putting the time back.
          */}
          {(
            <fieldset className="flex flex-col gap-2 rounded-xl border border-line bg-surface-2 px-4 py-3">
              <legend className="px-1 text-[12px] font-semibold text-ink-3">
                When it meets
              </legend>

              <div className="flex flex-wrap gap-2">
                <select
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  aria-label="Day"
                  className={`${field} w-[8.5rem]`}
                >
                  {DAYS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>

                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  aria-label="Start time"
                  className={`${field} w-[7.5rem]`}
                />

                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  aria-label="End time"
                  className={`${field} w-[7.5rem]`}
                />
              </div>

              <RoomPicker value={roomId} onChange={setRoomId} />

              <span className="text-[11.5px] text-ink-3">
                {startTime
                  ? 'Changing these moves every date it meets on.'
                  : 'With no start time it meets on no dates, and appears on no calendar.'}
              </span>
            </fieldset>
          )}

          {/*
            <b>How often, on every class</b> (D-603), which is deliberately the
            opposite of the fieldset above.

            Jackrabbit has no field for a cadence, so twelve real classes say
            `BIWKLY` in their name and nothing else. The import believes the
            word and cannot know which fortnight — only the teacher does. This
            is safe to offer on an imported class because no import has ever
            written a class's dates: `DiffClass` rewrites the day, the time,
            the room, the ages and the description, and touches neither the
            start date nor the cadence (D-124).

            <b>The first evening is the answer to "which week", not a second
            field for it.</b> A separate "meets on odd weeks" would need a
            definition of week one that nothing in the studio has, and would
            then disagree with the start date the moment either moved.
          */}
          {!data.meetsOnOneDate && (
          <fieldset className="flex flex-col gap-2 rounded-xl border border-line bg-surface-2 px-4 py-3">
            <legend className="px-1 text-[12px] font-semibold text-ink-3">How often it meets</legend>

            {/*
              <b>An explicit yes, because for some of the twelve the guess is
              already right</b> (D-603). Confirming is an answer — it is what
              claims the field against the next import and what takes the class
              off the list — but a native select fires no change event when the
              selected option is re-picked, so without this the one case that
              needs no correction could never be answered at all.
            */}
            {data.meetingWeekUnconfirmed && (
              <div className="flex flex-wrap items-center gap-2.5">
                <p className="min-w-0 flex-1 text-[12px] text-warn">
                  The name says this one meets every other week, so that is what the calendar
                  shows &mdash; but nobody has said which week. Set the first evening, or
                  confirm what is here.
                </p>

                <button
                  type="button"
                  onClick={() => setMeetingTouched(true)}
                  className="shrink-0 rounded-lg border border-warn px-2.5 py-1.5 text-[12px] font-semibold text-warn"
                >
                  That&rsquo;s right
                </button>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={cadence}
                onChange={(e) => {
                  setCadence(e.target.value as 'Weekly' | 'Fortnightly')
                  setMeetingTouched(true)
                }}
                aria-label="How often it meets"
                className={`${field} w-[11rem]`}
              >
                <option value="Weekly">Every week</option>
                <option value="Fortnightly">Every other week</option>
              </select>

              <label className="flex items-center gap-2 text-[12.5px] text-ink-2">
                {cadence === 'Fortnightly' ? 'First evening' : 'Starts'}
                <input
                  type="date"
                  value={firstMeeting}
                  onChange={(e) => {
                    setFirstMeeting(e.target.value)
                    setMeetingTouched(true)
                  }}
                  aria-label={cadence === 'Fortnightly' ? 'First evening' : 'Starts'}
                  className={`${field} w-[10.5rem]`}
                />
              </label>
            </div>

            <span className={`text-[11.5px] ${meetingProblem ? 'text-crit' : 'text-ink-3'}`}>
              {meetingProblem ??
                (cadence === 'Fortnightly'
                  ? 'It meets on that evening and every second week after it. Move the date by a week to put it on the other fortnight.'
                  : 'It meets on its day every week from that date.')}
            </span>
          </fieldset>
          )}

          {/*
            <b>Why it can still look weekly after she makes it fortnightly</b>
            (D-687, pilot item 35).

            Changing the pattern re-syncs the dates and sweeps the ones that
            dropped off it — except any carrying something that would be lost.
            Those are kept deliberately, and nothing said so: the sync hands
            the list back and the endpoint threw it away, so the one moment it
            was knowable was the moment nobody was told.

            <b>Directly under the pattern controls</b>, because that is the
            edit that produces it and the question it answers is *"I changed
            this, why has nothing changed"*.

            <b>Reported, never corrected</b> — the shape `/filing-conflicts`
            already uses (D-506). Only the studio knows whether a leftover is a
            mistake or a night that genuinely happened.
          */}
          {data.offPattern.length > 0 && (
            <section className="rounded-xl border border-warn bg-warn-bg px-4 py-3">
              <h3 className="text-[12px] font-bold tracking-[0.08em] text-ink-2 uppercase">
                On dates it no longer meets
              </h3>

              {/*
                <b>The past half first, because it is the half that is not a
                problem.</b> A session that has run and been marked is not a
                leftover — the class met, the dancers were marked, and it counts
                towards every one of their percentages. Calling it off would be a
                lie about something that happened and would move the number that
                decides who competes.
              */}
              {data.offPattern.some((s) => s.happened) && (
                <p className="mt-1.5 text-[12.5px] text-ink-2">
                  <strong>
                    {data.offPattern.filter((s) => s.happened).length} already ran.
                  </strong>{' '}
                  They stay on the calendar because they happened &mdash; there is nothing to put
                  right.
                </p>
              )}

              {/*
                <b>What it does not say is the point</b>, and an audit is why.

                This read *"they were kept because somebody has arranged cover
                for them or been assigned to them"*, which names two of the five
                things that keep a session back. The other three are a marked
                register, a private repair, and the teacher's own cancellation
                (D-355) &mdash; and that last one is the case this change's own
                integration test seeds, so the screen mis-explained the exact
                scenario the test pins. It also promised *"they will go on the
                next change"*, which is false for a teacher-cancelled row: that
                counts as recorded data for ever and no sync will remove it.

                D-261's complaint, in its own words: a screen confidently naming
                the wrong cause is worse than no reason, because somebody acts
                on it. So the sentence says only what is true of all five, and
                the chip on each row carries what is actually attached to it.
              */}
              {data.offPattern.some((s) => !s.happened) && (
                <p className="mt-1.5 text-[12.5px] text-ink-2">
                  <strong>
                    {data.offPattern.filter((s) => !s.happened).length} are still to come.
                  </strong>{' '}
                  {/*
                    <b>Attendance, not "a register"</b> (D-378). The studio says
                    attendance; the word register is kept for enrolment, which
                    is Jackrabbit's. `registerWording.test.ts` caught this
                    sentence on the full run after the filtered ones were green
                    — a rename scoped by eye three times and wrong every time is
                    why that check exists rather than a rule.
                  */}
                  They were kept because something is attached to them that would be lost &mdash;
                  attendance, cover, a substitute, or a night called off.
                </p>
              )}

              <ul className="mt-2 flex flex-wrap gap-1.5">
                {data.offPattern.map((session) => (
                  <li
                    key={session.occurrenceId}
                    className="rounded border border-line-2 bg-surface px-2 py-1 text-[11.5px] text-ink-2"
                  >
                    {formatFullDate(session.date)}
                    {session.isCancelled && ' · called off'}
                    {session.marked > 0 && ` · ${session.marked} marked`}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/*
            <b>Two columns once there is room for two</b> (D-566).

            The split is by what a person came to do, not by length: filing
            on the left, because it is the long half and the one an import
            leaves work in; everything about the class as a thing families
            see on the right. One column below Tailwind's `lg`, which is
            1024px here, and there the order below is the reading order.

            `items-start` so a short right column does not stretch to match
            a long left one, which would leave a card of empty surface under
            the last field.
          */}
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.12fr_0.88fr]">
            <div className="flex min-w-0 flex-col gap-4">

              {/*
                **From the class, and only from the class** (D-390).

                The list groups by kind, so filing a class under a new one
                regrouped it, moved the row, and left this select showing another
                group value while having saved the right one.

                The prop used to sit behind a `??` as an anti-flash fallback. It
                could never fire - this whole body is inside the `data &&` guard,
                so it renders only once the class has arrived - and `??`
                catches **null** as well as undefined, so the one thing it did was
                swallow the deliberate null that means *in no kind*. Taking a
                class out of every kind saved correctly and then snapped straight
                back to the row's value.
              */}
              <KindPicker
                  classId={classId}
                  filed={data.classTypeId}
                  kinds={kinds}
                  refused={kindsRefused}
                />

              {/*
                Editable, and no longer in the read-only block above (D-206).
                Ages were never in an export at all — they are parsed out of the
                class *name*, so they are a good guess that is sometimes wrong,
                and until now the screen showed the guess under a heading telling
                her to fix it in Jackrabbit, where there is nothing to fix.
              */}
              <fieldset className="flex flex-col gap-1">
                <legend className="text-[12px] font-semibold text-ink-3">Ages</legend>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={minAge}
                    onChange={(e) => setMinAge(e.target.value)}
                    placeholder="Any"
                    aria-label="Youngest age"
                    className={`${field} tabular w-20 text-center`}
                  />
                  <span className="text-[13px] text-ink-3">to</span>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={maxAge}
                    onChange={(e) => setMaxAge(e.target.value)}
                    placeholder="Any"
                    aria-label="Oldest age"
                    className={`${field} tabular w-20 text-center`}
                  />
                  <span className="text-[13px] text-ink-3">years</span>
                </div>

                {ageProblem ? (
                  <span className="text-[11.5px] text-crit">{ageProblem}</span>
                ) : (
                  <span className="text-[11.5px] text-ink-3">
                    {data.ageRangeIsOurs
                      ? 'Yours. The next import will leave this alone.'
                      : 'Worked out from the class name. Change it and it becomes yours.'}
                  </span>
                )}
              </fieldset>

              {/*
                What this class is for makeup purposes (D-280).

                All three arrive as guesses — the type from Jackrabbit's category,
                the style and level parsed out of the class name (D-272). Five real
                classes disagree with their own category and a fifth of the
                technique catalogue carries no readable level, and until this
                existed there was nowhere to fix any of it. A wrong level does not
                look like anything; it just quietly withholds a makeup a family
                is owed.
              */}
              <fieldset className="flex w-full flex-col gap-1">
                <legend className="text-[12px] font-semibold text-ink-3">Makeups</legend>

                {/*
                  <b>Named on screen, not by placeholder</b> (D-497).

                  All three were identified by the <i>Makeups</i> legend above
                  plus a placeholder — and a placeholder disappears the moment
                  the box is filled. So on a class that has a style the panel
                  showed a bare word (<i>Tap</i>, <i>Ballet</i>) with nothing
                  saying what it was, which is most of why the owner asked
                  whether four controls here mean the same thing. Three of them
                  were not named at all.

                  <b>And the first one is renamed.</b> Its accessible name was
                  <i>"What kind of class this is"</i>, one line from a visible
                  <i>Kind of class</i> that means something else entirely — a
                  screen reader announced them as the same question, and the
                  test guarding this file already had to disambiguate with an
                  anchored <code>/^kind of class/i</code>. It is not what kind of
                  class this is; it is what a missed session of it may be
                  repaired by.
                */}
                <div className="flex w-full flex-wrap items-end gap-2">
                  <label className="flex min-w-[7.5rem] flex-col gap-1">
                    <span className="text-[11.5px] font-semibold text-ink-3">Repaired by</span>
                    <select
                      value={makeupCategory}
                      onChange={(e) => setMakeupCategory(e.target.value)}
                      aria-label="How a missed session of this class is made up"
                      className={control}
                    >
                      <option value="">Not set</option>
                      <option value="Tech">Technique</option>
                      <option value="Choreo">Choreography</option>
                      <option value="Recital">Recital</option>
                    </select>
                  </label>

                  <label className="flex min-w-[7rem] flex-1 flex-col gap-1">
                    <span className="text-[11.5px] font-semibold text-ink-3">Style</span>
                    <input
                      value={style}
                      onChange={(e) => setStyle(e.target.value)}
                      placeholder="Ballet, tap, jazz…"
                      aria-label="The style this class covers"
                      className={control}
                    />
                  </label>

                  <label className="flex w-24 flex-col gap-1">
                    <span className="text-[11.5px] font-semibold text-ink-3">Level</span>
                    <input
                      value={levelBand}
                      onChange={(e) => setLevelBand(e.target.value)}
                      placeholder="3/4"
                      aria-label="Levels this class covers"
                      className={`${control} tabular text-center`}
                    />
                  </label>
                </div>

                <span className="text-[11.5px] text-ink-3">
                  {data.makeupCategoryIsOurs && data.levelBandIsOurs && data.styleIsOurs
                    ? 'Yours. The next import will leave these alone.'
                    : 'Worked out from the class name. Change them and they become yours.'}
                </span>

                {/*
                  <b>A tick rather than a fourth option in the select</b> (D-658).

                  That box says what the class *is* and comes from Jackrabbit;
                  this says what the studio asks of it. Folding a policy into
                  the category is what D-227 forbids, and the next import would
                  take it back besides.

                  The sentence under it is the whole rule, because the two
                  halves are not obvious from each other: no makeup is the part
                  she is ticking, and leaving the 80% is the part she is not.
                */}
                <label className="mt-1 flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={noMakeup}
                    onChange={(e) => setNoMakeup(e.target.checked)}
                    className="mt-0.5 size-4 shrink-0 accent-accent"
                  />
                  <span className="text-[12.5px]">
                    <span className="font-semibold">No makeup needed</span>
                    <span className="block text-[11.5px] text-ink-3">
                      Attendance is still taken and still shown. A missed session of this one is
                      never made up, and it stays out of the 80% that decides who competes.
                    </span>
                  </span>
                </label>


                {/*
                  Said once, here, because the studio writes combined levels and a
                  box that silently only took one would be wrong in a way nobody
                  could see.
                */}
                <span className="text-[11.5px] text-ink-3">
                  Levels can be combined &mdash; write <span className="tabular">2/3</span> for a
                  class that covers both. Leave it empty for a class with no level.
                </span>

                {/*
                  **Which of the two makeup blocks this is** (D-411).

                  `asked`: *"Once a makeup is scheduled, how are parents signing
                  up? it is not showing as an option on a parents account."* She
                  had filled this block in, named the class "ballet 4 makeup",
                  and reasonably concluded she had made one. She had not: this
                  says what a missed session of *this* class may be repaired by,
                  and the block below is what puts a session on a family's list.

                  The distinction was already written down &mdash; in a code
                  comment, one line away, saying *"those say what this class is,
                  this says what one Saturday is for"*. It had simply never been
                  said on the screen.
                */}
                <span className="text-[11.5px] text-ink-3">
                  This is about absences <em>from</em> this class &mdash; what a dancer who missed
                  it may make up. To offer one of its sessions <em>to</em> other dancers, use
                  &ldquo;Offer a session as a makeup&rdquo; below.
                </span>

                {/*
                  **The name and the category disagree** (D-415).

                  `asked`, on a screenshot of a parent's screen: *"tech classes
                  should be normal makeups, not privates. can you check why this
                  is listed as private."* The answer was the data &mdash;
                  `COMP_TECH_TAP` is filed in Jackrabbit as a comp choreo class,
                  and D-405 sends choreo to privates.

                  **The import has warned about this from the beginning**, and
                  that warning is only on screen while an import is running, so
                  nobody had seen one in months. It cost nothing while the two
                  categories merely matched differently; since D-405 it decides
                  whether a family pays.

                  Reported, never corrected (D-51, D-227): only the studio knows
                  whether the name or the dropdown is wrong.
                */}
                {/*
                  <b>It offers no makeups at all, and nothing said so</b>
                  (D-495).

                  A blank style makes `MakeupMatch` refuse every absence from
                  this class before the level is even looked at — so a family
                  who misses one is offered nothing, forever, and the screen that
                  could explain it shows an empty box with a placeholder.
                  `observed`: 153 of 186 real classes carry no style and 28 of
                  those take the group path, covering 147 enrolments.

                  Above the name-versus-category warning deliberately: that one
                  says a family is sent down the wrong road, this one says there
                  is no road.
                */}
                {data.offersNoMakeups && (
                  <span className="mt-1 rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[11.5px] text-crit">
                    This class has no <strong>style</strong>, so nobody who misses it can be
                    offered a makeup &mdash; a session only repairs an absence of the same style.
                    Fill it in above and its families become eligible.
                  </span>
                )}

                {data.nameDisagreesWithCategory && (
                  <span className="mt-1 rounded-lg border border-warn bg-warn-bg px-3 py-2 text-[11.5px] text-warn">
                    This class is <strong>named</strong> as technique but <strong>filed</strong> as{' '}
                    {data.makeupCategory === 'Choreo' ? 'choreography' : 'recital'}. Families are
                    offered a <strong>private</strong> to make it up, not a makeup class. Change it
                    here if the filing is wrong, or fix the name in Jackrabbit.
                  </span>
                )}
              </fieldset>

              {/*
                ⚠ <b>The tick that makes a rehearsal call possible at all</b> (D-842).

                <b>Its own fieldset, not the Makeups one above it.</b> It is the same
                *shape* as <i>No makeup needed</i> — a policy the studio sets, which
                Jackrabbit has no field for and no import can take back — which is why it
                reads well next to it. But nothing about a rehearsal call is a makeup, and
                inside that fieldset a screen reader announced it under <i>Makeups</i>.
                An audit caught the grouping after the placement was already right.

                <b>Without this control the whole feature is unreachable</b> — the declare
                route answers *this class does not take a call*, whose own refusal says an
                admin can turn it on. Nothing could: shipped, tested, audited, no way in,
                which is the reopen-button shape rule 2 exists for.
              */}
              <fieldset className="flex w-full flex-col gap-1">
                <legend className="text-[12px] font-semibold text-ink-3">Rehearsal calls</legend>

                <label className="mt-1 flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={takesACall}
                    onChange={(e) => setTakesACall(e.target.checked)}
                    className="mt-0.5 size-4 shrink-0 accent-accent"
                  />
                  <span className="text-[12.5px]">
                    <span className="font-semibold">A sub-team can be left off a session</span>
                    <span className="block text-[11.5px] text-ink-3">
                      For a rehearsal where not everybody is called every week. Whoever teaches
                      it can then mark a whole sub-team as not called for one session, so nobody
                      is absent for a night they were not asked to.
                    </span>
                  </span>
                </label>
              </fieldset>

              {/*
                Offering one of this class's sessions as a makeup (D-281).

                Below the class's own makeup fields because it is a different
                question — those say what this class *is*, this says what one
                Saturday is *for*. And it saves separately: a failed designation
                must not look like a failed class edit.
              */}
              {/*
                <b>A section, not a field</b> (D-510).

                Its legend was `text-[12px] font-semibold text-ink-3` &mdash;
                character for character what <i>Routine name</i> directly below
                it uses. So the one part of this panel that is a whole small
                workflow (a scrolling date list, four controls and an act that
                changes what families see) was announced at exactly the weight of
                a single text box, and ran straight into the next field with
                nothing between them. That is the *easily missed* half of the
                report; the button was only the *confusing* half.

                The treatment is already on this panel &mdash; <i>When it
                meets</i>, the other multi-control group, is a bordered card.
                Reusing it rather than inventing a third look, because two
                spellings of &ldquo;this is a section&rdquo; is how a panel stops
                having a grammar at all.
              */}
              <fieldset className="flex w-full flex-col gap-1 rounded-xl border border-line bg-surface-2 px-4 py-3">
                <legend className="px-1 text-[12px] font-semibold text-ink-3">
                  Offer a session as a makeup
                </legend>
                <MakeupSessions
                  classId={classId}
                  sessions={data.sessions}
                  isOurs={data.isOurs}
                  meetsOnOneDate={data.meetsOnOneDate}
                />

                {/*
                  <b>Run this one again</b> (D-837). Only for a make-up session:
                  a class that meets on one date, made here, whose session is
                  designated. A season class with offers on some nights is not a
                  make-up class and has nothing to run again.
                */}
                <RunItAgain data={data} />
              </fieldset>

              {/*
                With the filing rather than with the routine (D-566). It is the
                one field here that is prose, and the left column is otherwise
                much the shorter of the two — laid out the other way the right
                ran a third of a screen past the left, which reads as a column
                that failed to load rather than as one that is simply shorter.
              */}
              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-semibold text-ink-3">
                  Description &mdash; just for this class
                </span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Usually empty. The description families read belongs to the kind of class."
                  className={`${field} leading-relaxed`}
                />
                {/*
                  Steered away from rather than removed. In the real export the
                  descriptions are near-identical within a kind — seven copies of
                  what is really the kind's copy — so this is for the genuine
                  exception, and the kind's blurb is the normal answer (D-205).
                */}
                <span className="text-[11.5px] text-ink-3">
                  Leave this blank unless this one class differs from the rest of its kind.
                </span>
              </label>

            </div>

            {/*
              The right column: what the class <i>is</i>, rather than how it
              is filed. The routine and its music, the videos families open,
              the description they read, and whether they see it at all.
            */}
            <div className="flex min-w-0 flex-col gap-4">

              {/*
                <b>The class's own name, for a class the studio made</b> (D-633).

                Reported from the pilot: <i>"need to be able to edit the name of
                a class after it is made"</i>. Only where the name is ours to
                change - an imported one is Jackrabbit's administrative string
                and the enrolment stage matches rows on it, so renaming it here
                would stop that class's new enrolments arriving, a term later
                and silently. The server refuses it either way; this is the
                half that does not offer what would be refused (D-333).
              */}
              {data.isOurs && (
                <label className="flex flex-col gap-1">
                  <span className="text-[12px] font-semibold text-ink-3">Class name</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={field}
                  />
                  <span className="text-[11.5px] text-ink-3">
                    What the studio calls this class. You made this one, so it is yours to
                    change.
                  </span>
                </label>
              )}

              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-semibold text-ink-3">Routine name</span>
                <input
                  value={routineName}
                  onChange={(e) => setRoutineName(e.target.value)}
                  placeholder="Golden Huntrix"
                  className={field}
                />
                <span className="text-[11.5px] text-ink-3">
                  What it is called on stage &mdash; how a dancer finds their music, and how a
                  competition running order names it.
                </span>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-semibold text-ink-3">Music folder</span>
                <input
                  value={musicUrl}
                  onChange={(e) => setMusicUrl(e.target.value)}
                  placeholder="https://www.dropbox.com/..."
                  className={field}
                />
                {urlProblem ? (
                  <span className="text-[11.5px] text-crit">{urlProblem}</span>
                ) : (
                  <span className="text-[11.5px] text-ink-3">
                    Reachable from the tablet in the room without signing in.
                  </span>
                )}
              </label>

              {/*
                Practice videos (D-541): the same rows the teacher edits from her
                own class view, here for Tonya. Families see them; the tablet
                never does. Each address takes the same check as the music link.
              */}
              <fieldset className="flex flex-col gap-1">
                <legend className="text-[12px] font-semibold text-ink-3">Practice videos</legend>
                <div className="flex flex-col gap-2">
                  {choreoLinks.map((row, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <input
                        value={row.label}
                        onChange={(e) =>
                          setChoreoLinks(choreoLinks.map((l, j) => (j === i ? { ...l, label: e.target.value } : l)))
                        }
                        placeholder={`Part ${i + 1}`}
                        aria-label={`Label for practice video ${i + 1}`}
                        className={`${field} basis-32 grow-0`}
                      />
                      {/*
                        A video a teacher uploaded has no address to edit
                        (D-668). It says so rather than offering an empty box,
                        which would read as a link somebody forgot to paste —
                        and it stays removable, because taking one down is the
                        one thing Tonya may need to do to it.
                      */}
                      {isChoreoFile(row) ? (
                        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[12.5px] font-semibold text-ink-2">
                          <Video size={13} strokeWidth={2.5} className="shrink-0" />
                          Uploaded video
                        </span>
                      ) : (
                        <input
                          value={row.url}
                          onChange={(e) =>
                            setChoreoLinks(choreoLinks.map((l, j) => (j === i ? { ...l, url: e.target.value } : l)))
                          }
                          placeholder="https://youtu.be/..."
                          aria-label={`Address of practice video ${i + 1}`}
                          className={`${field} min-w-0 flex-1`}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setChoreoLinks(choreoLinks.filter((_, j) => j !== i))}
                        aria-label={`Remove practice video ${i + 1}`}
                        className="py-1 text-[12px] font-semibold text-ink-3 underline underline-offset-2"
                      >
                        Remove
                      </button>
                      {musicUrlProblem(row.url) && (
                        <span className="basis-full text-[11.5px] text-crit">{musicUrlProblem(row.url)}</span>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setChoreoLinks([...choreoLinks, { label: '', url: '', mediaAssetId: null }])}
                  className="self-start py-1 text-[12px] font-semibold text-accent underline underline-offset-2"
                >
                  {choreoLinks.length === 0 ? 'Add a practice video' : 'Add another part'}
                </button>
                <span className="text-[11.5px] text-ink-3">
                  YouTube links families can open &mdash; set each video to <strong>Unlisted</strong>, not Private.
                  Adding one rings the bell for every family in the class.
                </span>
              </fieldset>

              <div className="flex flex-col gap-1.5 rounded-lg border border-line-2 bg-surface px-3 py-2.5">
                <label className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={onSite}
                    onChange={(e) => setOnSite(e.target.checked)}
                    className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-semibold">Show on the website</span>
                    <span className="mt-0.5 block text-[12px] text-ink-3">
                      Set from the class category when it was imported &mdash; change it here, and
                      the next import will leave your choice alone.
                    </span>
                  </span>
                </label>

                {/*
                  What the sentence above used to claim on its own (D-261).

                  It read "Families can see this class and register for it" beside
                  a tick that three other conditions could silently overrule — a
                  competitive programme, a kind switched off, or no room. An
                  unconditional promise over a conditional behaviour is the same
                  fault D-204 removed a control for, so the promise is gone and
                  the condition is named instead.
                */}
                {onSite && blocked.length > 0 && (
                  <div className="rounded-lg bg-warn-bg px-2.5 py-2 text-[12px] text-ink-2">
                    <b className="font-semibold">Ticked, but not on the website.</b>
                    <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-4">
                      {blocked.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {onSite && data.notOnSiteBecause.length === 0 && (
                  <p className="text-[12px] text-ink-3">
                    Families can see this class and register for it.
                  </p>
                )}
              </div>

              {/*
                **A way from the headcount to the people it counts** (D-440).

                D-300 closed exactly this on the teacher's side and left it open
                here: the record said how many were enrolled and offered no way
                to ask who they were, so Tonya's only route to a roster was the
                calendar - find the right Wednesday, click the block. She is the
                one with the 64-dancer class, so the person who asked for this
                was the person it did not serve.

                Hidden at nought rather than shown empty: an empty roster is a
                true answer to a question nobody has, and a control that leads
                somewhere blank reads as broken.

                At the foot of the right column since D-566: it is the one thing
                here that leaves the record, so it stops sitting between the
                facts and the fields.
              */}
              {data.enrolled > 0 && mayMeasure && (
                <Link
                  to={`/classes/${classId}/measure`}
                  className="inline-flex min-h-[44px] items-center gap-2 self-start rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
                >
                  <Ruler size={14} strokeWidth={2.5} className="shrink-0" />
                  Measure the class
                </Link>
              )}

            </div>
          </div>

          {save.isError && (
            <p role="alert" className="text-[12.5px] text-crit">
              {(save.error as Error).message}
            </p>
          )}
        </div>
      )}

      {data && (
        /*
          <b>Still sticky, and now to the foot of the window</b> (D-566). It
          was pinned to the bottom of a drawer that scrolled inside itself;
          on a page the record is what scrolls, so this keeps Save in reach
          without it being a bar floating over a list. The negative margins
          let it span the shell's padding, so it reads as a bar rather than
          as one more card.

          <b><code>bottom-14</code> on a phone, because something else already
          owns <code>bottom-0</code> there.</b> `NavRail`'s bottom bar is
          `fixed bottom-0 z-40 sm:hidden`, so at `bottom-0` this sat
          underneath it and Save was unreachable on the one device where
          reaching it matters most. Seen in a screenshot, not by reading:
          both elements are correct on their own.

          The page carries `pb-28` for the same reason the classes list does
          — a sticky bar overlays what is behind it, so the last field needs
          somewhere to scroll to.
        */
        <div className="sticky bottom-14 -mx-5 mt-4 flex items-center gap-3 border-t border-line bg-surface px-5 py-3.5 sm:bottom-0">
          {/*
            <b>A button that says "Saved" is not a button</b> (D-505).
            `asked`: *"The save button doesn't work. Does it auto save? and if
            it does why do we have the save button?"*

            Both halves of that were fair. This panel really does have two
            behaviours — the typed fields wait for Save, while the kind
            picker and the offered sessions write immediately because they
            are different records — and the button reported the *past* in the
            greyed-out state, so somebody who had just changed one of the
            instant controls saw a dead button labelled "Saved" and read it as
            broken.

            <b>The button now only ever says what it does.</b> Nothing to
            save is said beside it, in words, rather than by relabelling the
            control — the state belongs to the panel, not to the verb.
          */}
          <button
            type="button"
            disabled={!dirty || urlProblem !== null || ageProblem !== null || meetingProblem !== null || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-on-accent disabled:opacity-40"
          >
            {save.isPending ? 'Saving...' : 'Save'}
          </button>

          {!dirty && !save.isPending && (
            <span className="text-[12px] text-ink-3">
              {save.isSuccess ? 'Saved.' : 'Nothing to save.'}
            </span>
          )}

          {/*
            No <i>Close</i> beside Save any more (D-566). It closed a drawer;
            on a page the way out is the way in — <i>All classes</i> at the
            top, and the browser's own back. A second control that only
            navigates would be a button competing with a link for the same
            job.

            **Removing a class made by mistake** (D-413). `asked`, on a
            screenshot of one with no teacher and nobody in it: *"we need a
            way to delete a class if she no longer want it."*

            Pushed to the far end and unaccented: it is the one thing on this
            panel that cannot be undone by typing something else.
          */}
          {/*
            ⚠ <b>Offered for an imported class too</b> (D-859). It was `data.isOurs`, so a
            class Jackrabbit had **deleted** could be removed from neither side: the import
            never archives on absence, deliberately, and this button was not there. Found
            in production on placeholder spot holders that survived a fresh import.

            The risk the guard named is real only while Jackrabbit still has the class, so
            it is a sentence in the confirm now rather than a hidden control.
          */}
          <button
            type="button"
            onClick={() => setRemoving(true)}
            className="ml-auto text-[12.5px] font-semibold text-crit hover:underline"
          >
            Remove this class
          </button>
        </div>
      )}

      {removing && data && (
        <RemoveClass
          classId={classId}
          name={data.name}
          onClose={() => setRemoving(false)}
          onRemoved={() => {
            setRemoving(false)
            // The record is gone, so there is nothing to come back to:
            // replace rather than push, or Back lands on a 404 (D-566).
            navigate('/admin/classes', { replace: true })
          }}
        />
      )}
  </div>
)
}

/**
 * Which kind this class is filed under.
 *
 * Saves on change rather than waiting for the panel's Save, because it is the
 * only field here that is a relationship rather than text — and it is the same
 * action the list offers in bulk, which applies immediately too.
 */
function KindPicker({
  classId,
  filed,
  kinds,
  refused = false,
}: {
  classId: string
  filed: string | null
  /**
   * The kinds, or <c>undefined</c> while they have not arrived (D-566).
   *
   * <b>Not the same as none, and collapsing the two says something untrue
   * about the class</b> (D-209). The record and the kinds are two requests;
   * with the record on its own page the first load is the normal path, and
   * every database-backed endpoint refuses for ~75 seconds after a deploy
   * (D-33). Handed <c>[]</c> for "not yet", a filed class rendered a select
   * whose only option was <i>Not in a kind</i> and sat on it — a positive,
   * wrong statement, on the one control here that writes the moment it is
   * changed. `retry: 1` means the refused case never recovers.
   */
  kinds: ClassTypeView[] | undefined
  /**
   * Whether asking for them has already failed.
   *
   * <b>A refusal that has finished is not still loading.</b> Without this the
   * field says <i>Loading the kinds...</i> for ever after a 500 — `retry: 1`
   * is exhausted and nothing else will arrive — which is the ~75 seconds after
   * a deploy (D-33), the case this whole distinction was drawn for.
   */
  refused?: boolean
}) {
  const queryClient = useQueryClient()

  const move = useMutation({
    mutationFn: (kindId: string | null) => assignClass(classId, kindId),
    onSuccess: (types) => {
      queryClient.setQueryData(['class-types'], types)
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })
      /*
       * <b>The class itself, which is where this control reads from</b> — the
       * half D-390 left behind, and reported twice as "changed the class type
       * and it doesn't actually change".
       *
       * D-390 moved the filed kind off the list row and onto the class,
       * correctly: the list groups by kind, so filing a class moved its row out
       * from under the open panel and the panel then showed another group's
       * value. But this mutation kept refreshing only the list. So the select
       * re-rendered from a cache nobody had touched and snapped back — which
       * looks exactly like a save that failed, and is why fixing what the panel
       * *displayed* never fixed it. The write has always worked.
       */
      queryClient.invalidateQueries({ queryKey: ['admin-class', classId] })
    },
  })

  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] font-semibold text-ink-3">Kind of class</span>
      {kinds === undefined ? (
        /*
          Says which of the two it is, rather than showing a control that
          cannot tell the truth yet. Disabled rather than absent, so the field
          keeps its place and the screen does not jump when they arrive.
        */
        <select disabled value="" className={field}>
          <option value="">
            {refused ? 'The kinds could not be loaded' : 'Loading the kinds...'}
          </option>
        </select>
      ) : (
        <select
          value={filed ?? ''}
          onChange={(e) => move.mutate(e.target.value || null)}
          className={field}
        >
          <option value="">Not in a kind</option>
          {kinds.map((k) => (
            <option key={k.classTypeId} value={k.classTypeId}>
              {k.name}
            </option>
          ))}
        </select>
      )}
      <span className="text-[11.5px] text-ink-3">
        The heading it appears under on the public class page.
      </span>
    </label>
  )
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * A `<time>` input wants `HH:mm`; the API sends `HH:mm:ss`.
 *
 * Trimmed rather than parsed — a Date round-trip here would drag the browser's
 * timezone into a value that is a wall-clock time, and a class does not move an
 * hour in the spring (D-68).
 */
function clockValue(time: string | null): string {
  return time ? time.slice(0, 5) : ''
}

function RoomPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const { data } = useQuery({ queryKey: ['new-class-options'], queryFn: fetchNewClassOptions })

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Room"
      className={field}
    >
      <option value="">No room yet</option>
      {data?.rooms.map((room) => (
        <option key={room.id} value={room.id}>
          {room.name}
        </option>
      ))}
    </select>
  )
}

/**
 * One fact in the strip across the top (D-566).
 *
 * <b>Each pair is its own cell now, not two cells in a shared grid.</b> Laid
 * out across, a bare <c>dt</c>/<c>dd</c> pair would let one long value push its
 * own label into the next column — so the pair is wrapped in a <c>div</c>,
 * which a <c>dl</c> permits and which keeps a label with the thing it names.
 * No border and no fill of its own: the strip is one surface, so a track the
 * facts do not reach is simply empty.
 */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-bold tracking-[0.09em] text-ink-3 uppercase">{label}</dt>
      <dd className="mt-0.5 wrap-anywhere font-semibold">{children}</dd>
    </div>
  )
}

const Missing = ({ children }: { children: React.ReactNode }) => (
  <span className="text-ink-3">{children}</span>
)

/**
 * Who teaches a class, and changing it (D-537).
 *
 * **Read-only until now, and unchangeable anywhere.** The import builds classes
 * with two teachers and always has — ten of the studio's 184 carry two — while
 * the app could set one at creation and never afterwards, and the import does
 * not update instructors on an existing class either. So a class naming a
 * teacher who left could not be corrected in either system.
 *
 * **The whole set is sent, not an add or a remove.** Swapping a teacher in two
 * round trips leaves a window where the class has nobody.
 */
function TeacherSet({
  classId,
  names,
  staffIds,
  assistingIds,
}: {
  classId: string
  names: string[]
  staffIds: string[]
  /** Of those, the ones helping rather than teaching (D-538). */
  assistingIds: string[]
}) {
  const canManage = useCan('ManageClasses')

  const [editing, setEditing] = useState(false)
  const [chosen, setChosen] = useState<string[]>(staffIds)
  const [assisting, setAssisting] = useState<string[]>(assistingIds)

  const queryClient = useQueryClient()

  const { data: staff } = useQuery({
    queryKey: ['staff'],
    queryFn: fetchStaff,
    enabled: editing,
  })

  const save = useMutation({
    mutationFn: () => setClassTeachers(classId, chosen, assisting.filter((id) => chosen.includes(id))),
    onSuccess: () => {
      setEditing(false)

      /*
       * <b>The keys the screen actually reads</b>, which these were not: this
       * invalidated `['class', id]` and `['classes']`, and no query anywhere
       * in the app uses either - so a teacher added to a class did not appear
       * until the page was reloaded, reported from the pilot. Every sibling
       * control on this panel names these two.
       *
       * The public catalog needs nothing from here: the server refreshes it
       * on the way out, because checking your own work has to show your own
       * work (D-210).
       */
      queryClient.invalidateQueries({ queryKey: ['admin-class', classId] })
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })
    },
  })

  if (!editing) {
    /*
     * **The role is shown, not just the names.** A class taught by Hannah with
     * Ava helping reads differently from one they both teach — and what a
     * student teacher may do is materially narrower (D-538), so a panel that
     * rendered them identically would be hiding the thing somebody opened it
     * to check.
     *
     * **Asked of the id at the same index, never of the name** (D-82). The
     * studio has two Ashleys, and the server falls back to a shared
     * `Unassigned` for anybody archived — so a set of names marks both when
     * only one of them assists, which is a confident wrong sentence about who
     * may take attendance.
     */
    return (
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {names.length > 0 ? (
          names.map((name, i) => (
            <span key={staffIds[i] ?? name}>
              {name}
              {assistingIds.includes(staffIds[i]) && (
                <span className="text-ink-3"> &middot; assisting</span>
              )}
              {i < names.length - 1 && ','}
            </span>
          ))
        ) : (
          <Missing>None yet</Missing>
        )}

        {canManage && (
          <button
            type="button"
            onClick={() => {
              setChosen(staffIds)
              setAssisting(assistingIds)
              setEditing(true)
            }}
            className="py-1 text-[12px] font-semibold text-ink-3 underline underline-offset-2"
          >
            Change
          </button>
        )}
      </span>
    )
  }

  const toggle = (id: string) =>
    setChosen((was) => (was.includes(id) ? was.filter((x) => x !== id) : [...was, id]))

  const setRole = (id: string, helps: boolean) =>
    setAssisting((was) => (helps ? [...new Set([...was, id])] : was.filter((x) => x !== id)))

  return (
    <div className="mt-1">
      <p className="mb-1.5 text-[12px] text-ink-3">
        Tick everybody on it. The first one is the name shown first &mdash; and say
        whether each is teaching or helping.
      </p>

      <div className="max-h-52 overflow-y-auto rounded-lg border border-line">
        {(staff ?? []).map((s) => (
          <div
            key={s.staffId}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line px-2.5 py-2 text-[13px] first:border-t-0"
          >
            <label className="flex min-w-0 flex-1 items-center gap-2">
              <input
                type="checkbox"
                checked={chosen.includes(s.staffId)}
                onChange={() => toggle(s.staffId)}
              />
              <span className="wrap-anywhere">{s.name}</span>
            </label>

            {/*
              Offered only once she is on the class. A role picker beside
              somebody who is not assigned is a control with nothing to act on.
            */}
            {chosen.includes(s.staffId) && (
              <select
                value={assisting.includes(s.staffId) ? 'assists' : 'teaches'}
                onChange={(e) => setRole(s.staffId, e.target.value === 'assists')}
                aria-label={`What ${s.name} does on this class`}
                className="rounded-lg border border-line bg-surface-2 px-2 py-1 text-[12px]"
              >
                <option value="teaches">Teaches</option>
                <option value="assists">Helps</option>
              </select>
            )}
          </div>
        ))}
      </div>

      {assisting.some((id) => chosen.includes(id)) && (
        /*
         * Said plainly, because it is a real narrowing and Tonya is choosing it
         * from a two-word dropdown.
         */
        <p className="mt-2 text-[12.5px] text-ink-3">
          Somebody helping does not take attendance for this class and cannot call
          it off. She still sees it on her schedule, and she can be asked to cover.
        </p>
      )}

      {save.error && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          {(save.error as Error).message}
        </p>
      )}

      {chosen.length === 0 && (
        /*
         * Allowed, and said out loud. The studio already holds classes filed
         * with no teacher and 86 solos awaiting a slot (D-84), so refusing
         * would invent a rule the data does not follow — but leaving a class
         * with nobody by accident is worth one sentence.
         */
        <p className="mt-2 text-[12.5px] text-warn">
          Nobody is selected. The class will show as having no teacher.
        </p>
      )}

      <div className="mt-2.5 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate()}
          className="rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-60"
        >
          {save.isPending ? 'Saving...' : 'Save'}
        </button>

        <button
          type="button"
          onClick={() => setEditing(false)}
          className="rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * The route, which is the only thing that knows it is a route (D-566).
 *
 * <b>Kept separate from the record itself so the record is still a component.</b>
 * Five test files render it directly with a class id and a set of kinds; making
 * the screen read its own params would have made every one of them stand up a
 * router just to reach a field. This wrapper is the part that cannot be tested
 * without one.
 *
 * <b>It hands on `undefined` rather than an empty list</b> while the kinds are
 * in flight or refused — see the prop on `KindPicker`. Collapsing the two here
 * is the bug that shipped in the first cut of this screen, and no test of the
 * record could have seen it, because a component cannot see its call site.
 */
export function ClassRecordRoute() {
  const { classId = '' } = useParams()
  const kinds = useQuery({ queryKey: ['class-types'], queryFn: fetchClassTypes })

  return (
    <ClassRecordView classId={classId} kinds={kinds.data} kindsRefused={kinds.isError} />
  )
}
